import { useState, useCallback, useEffect } from 'react'
import type { EditorLayout, SectionKey, ZoneId, SectionCard, SectionSpacing } from '../types/editor'
import { DEFAULT_LAYOUT } from '../types/editor'
import type { TextSizeValue } from '../types/caseStudy'

const STORAGE_PREFIX = 'editor-layout'

function getStorageKey(caseStudyId?: string): string {
  return caseStudyId ? `${STORAGE_PREFIX}-${caseStudyId}` : STORAGE_PREFIX
}

function loadLayout(caseStudyId?: string): EditorLayout {
  const defaults = structuredClone(DEFAULT_LAYOUT)
  try {
    const raw = localStorage.getItem(getStorageKey(caseStudyId))
    if (raw) {
      const saved = JSON.parse(raw) as EditorLayout
      const zones = { ...defaults.zones }
      for (const zoneId of Object.keys(defaults.zones) as ZoneId[]) {
        if (saved.zones && Array.isArray(saved.zones[zoneId])) {
          zones[zoneId] = saved.zones[zoneId]
        }
      }
      const sectionSizes = { ...defaults.sectionSizes, ...(saved.sectionSizes || {}) }
      const sectionSpacing = { ...defaults.sectionSpacing, ...(saved.sectionSpacing || {}) }
      return { zones, sectionSizes, sectionSpacing }
    }
  } catch {
    // ignore parse errors
  }
  return defaults
}

function saveLayout(layout: EditorLayout, caseStudyId?: string): void {
  try {
    localStorage.setItem(getStorageKey(caseStudyId), JSON.stringify(layout))
  } catch {
    // ignore storage errors
  }
}

const useEditorLayout = (caseStudyId?: string) => {
  const [layout, setLayout] = useState<EditorLayout>(() => loadLayout(caseStudyId))

  useEffect(() => {
    setLayout(loadLayout(caseStudyId))
  }, [caseStudyId])

  const persist = useCallback((next: EditorLayout) => {
    setLayout(next)
    saveLayout(next, caseStudyId)
  }, [caseStudyId])

  const updateZone = useCallback((zoneId: ZoneId, cards: SectionCard[]) => {
    setLayout(prev => {
      const next = structuredClone(prev)
      next.zones[zoneId] = cards.map(c => c.key)
      saveLayout(next, caseStudyId)
      return next
    })
  }, [caseStudyId])

  const updateSectionSize = useCallback((key: SectionKey, size: TextSizeValue) => {
    setLayout(prev => {
      const next = structuredClone(prev)
      next.sectionSizes[key] = size as SectionCard['size']
      saveLayout(next, caseStudyId)
      return next
    })
  }, [caseStudyId])

  const updateSectionSpacing = useCallback((key: SectionKey, spacing: SectionSpacing) => {
    setLayout(prev => {
      const next = structuredClone(prev)
      if (!next.sectionSpacing) next.sectionSpacing = {} as Record<SectionKey, SectionSpacing>
      next.sectionSpacing[key] = spacing
      saveLayout(next, caseStudyId)
      return next
    })
  }, [caseStudyId])

  const toggleSectionVisibility = useCallback((_key: SectionKey) => {
    // Visibility is tracked per-card in the zone; this is a no-op placeholder
    // that callers can wire up when cards carry a `visible` flag.
  }, [])

  const resetLayout = useCallback(() => {
    const next = structuredClone(DEFAULT_LAYOUT)
    persist(next)
  }, [persist])

  return { layout, updateZone, updateSectionSize, updateSectionSpacing, toggleSectionVisibility, resetLayout }
}

export default useEditorLayout
